// BluetoothPanel — sensor connection UI in the sidebar.
// Shows connect/disconnect buttons for power meter and heart rate monitor,
// plus live readings when connected.

import React from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import BluetoothIcon from '@mui/icons-material/Bluetooth';
import BluetoothConnectedIcon from '@mui/icons-material/BluetoothConnected';
import FavoriteIcon from '@mui/icons-material/Favorite';
import BoltIcon from '@mui/icons-material/Bolt';
import { BluetoothState } from '../hooks/useBluetooth';
import { colors } from '../theme';

interface Props {
  ble: BluetoothState;
}

export default function BluetoothPanel({ ble }: Props) {
  return (
    <Box
      sx={{
        p: 2.5,
        borderRadius: 3,
        backgroundColor: colors.surface,
        border: `1px solid ${colors.border}`,
      }}
    >
      <Typography
        sx={{
          fontSize: '0.6875rem',
          fontWeight: 600,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: colors.textMuted,
          mb: 2,
        }}
      >
        Sensors
      </Typography>

      <Stack spacing={2}>
        {/* Power meter */}
        <Box>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BoltIcon
                sx={{
                  fontSize: 18,
                  color: ble.powerConnected ? colors.brand : colors.textFaint,
                }}
              />
              <Box>
                <Typography
                  sx={{ fontSize: '0.8125rem', fontWeight: 600, lineHeight: 1.2 }}
                >
                  {ble.powerConnected
                    ? ble.powerDevice || 'Power meter'
                    : 'Power meter'}
                </Typography>
                {ble.powerConnected && ble.power && (
                  <Typography
                    sx={{
                      fontFamily: '"JetBrains Mono", monospace',
                      fontSize: '0.75rem',
                      color: colors.brand,
                      fontWeight: 600,
                      mt: 0.25,
                    }}
                  >
                    {ble.power.watts} W
                    {ble.power.cadenceRpm !== null &&
                      ` · ${ble.power.cadenceRpm} rpm`}
                  </Typography>
                )}
              </Box>
            </Box>

            {ble.powerConnected ? (
              <Button
                size="small"
                onClick={ble.disconnectPower}
                sx={{
                  minWidth: 0,
                  px: 1.5,
                  py: 0.5,
                  fontSize: '0.6875rem',
                  color: colors.textMuted,
                  borderColor: colors.border,
                  '&:hover': {
                    borderColor: colors.danger,
                    color: colors.danger,
                  },
                }}
                variant="outlined"
              >
                Disconnect
              </Button>
            ) : (
              <Button
                size="small"
                onClick={ble.connectPower}
                startIcon={<BluetoothIcon sx={{ fontSize: 16 }} />}
                sx={{
                  minWidth: 0,
                  px: 1.5,
                  py: 0.5,
                  fontSize: '0.6875rem',
                }}
                variant="outlined"
              >
                Connect
              </Button>
            )}
          </Stack>
        </Box>

        {/* Heart rate */}
        <Box>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FavoriteIcon
                sx={{
                  fontSize: 18,
                  color: ble.hrConnected ? colors.danger : colors.textFaint,
                }}
              />
              <Box>
                <Typography
                  sx={{ fontSize: '0.8125rem', fontWeight: 600, lineHeight: 1.2 }}
                >
                  {ble.hrConnected
                    ? ble.hrDevice || 'HR monitor'
                    : 'Heart rate'}
                </Typography>
                {ble.hrConnected && ble.heartRate && (
                  <Typography
                    sx={{
                      fontFamily: '"JetBrains Mono", monospace',
                      fontSize: '0.75rem',
                      color: colors.danger,
                      fontWeight: 600,
                      mt: 0.25,
                    }}
                  >
                    {ble.heartRate.bpm} bpm
                  </Typography>
                )}
              </Box>
            </Box>

            {ble.hrConnected ? (
              <Button
                size="small"
                onClick={ble.disconnectHr}
                sx={{
                  minWidth: 0,
                  px: 1.5,
                  py: 0.5,
                  fontSize: '0.6875rem',
                  color: colors.textMuted,
                  borderColor: colors.border,
                  '&:hover': {
                    borderColor: colors.danger,
                    color: colors.danger,
                  },
                }}
                variant="outlined"
              >
                Disconnect
              </Button>
            ) : (
              <Button
                size="small"
                onClick={ble.connectHr}
                startIcon={<BluetoothIcon sx={{ fontSize: 16 }} />}
                sx={{
                  minWidth: 0,
                  px: 1.5,
                  py: 0.5,
                  fontSize: '0.6875rem',
                }}
                variant="outlined"
              >
                Connect
              </Button>
            )}
          </Stack>
        </Box>
      </Stack>

      {ble.error && (
        <Typography
          sx={{
            mt: 1.5,
            fontSize: '0.75rem',
            color: colors.danger,
          }}
        >
          {ble.error}
        </Typography>
      )}
    </Box>
  );
}
